import type { GeometryElement as JXGElement } from 'jsxgraph';
import type { BoundLabel } from '@examdojo/core/widget';
import type { PortMap, PointPort, EdgePort } from '../../../port';
import type { GeometryPadElementContext, CreatedGeometryElement } from '../../element.model';
import { FEATURE_LINE_DEFAULTS } from '../feature.defaults';
import type { MedianElement } from './median.model';

/**
 * Opposite-edge lookup for triangle vertices.
 * Vertex A → edge BC, vertex B → edge CA, vertex C → edge AB.
 */
const TRIANGLE_OPPOSITE_EDGE: Record<string, string> = {
  A: 'edge.BC',
  B: 'edge.CA',
  C: 'edge.AB',
};

/**
 * Create a median line from a triangle vertex to the midpoint of the opposite edge.
 *
 * Reads the vertex and opposite edge from the parent shape's port registry,
 * computes the midpoint of the opposite edge, and draws a dashed line.
 */
export function createMedian(
  element: MedianElement & { id: string },
  context: GeometryPadElementContext,
): CreatedGeometryElement {
  const { elementFactory, portRegistry, displayConfig } = context;
  const jsxObjects: JXGElement[] = [];
  const labels: BoundLabel[] = [];

  const parentId = element.params.of;
  const fromVertex = element.params.from;

  // Per-element display overrides
  const elementDisplay = displayConfig.elements?.[element.id];

  // Resolve the vertex position
  const getVertex = portRegistry.getGetter(`${parentId}.point.${fromVertex}`);

  // Determine the opposite edge port path
  const oppositeEdgeName = TRIANGLE_OPPOSITE_EDGE[fromVertex];
  if (!oppositeEdgeName) {
    throw new Error(`Median: unknown vertex "${fromVertex}" — must be A, B, or C.`);
  }
  const getEdge = portRegistry.getGetter(`${parentId}.${oppositeEdgeName}`);

  // Compute midpoint and draw the line at creation time
  const vertex = getVertex() as PointPort;
  const edge = getEdge() as EdgePort;
  const mid = midpoint(edge.start, edge.end);

  const lineStyle = {
    color: elementDisplay?.line?.color ?? FEATURE_LINE_DEFAULTS.color,
    strokeWidth: elementDisplay?.line?.strokeWidth ?? FEATURE_LINE_DEFAULTS.strokeWidth,
    dash: elementDisplay?.line?.dash ?? FEATURE_LINE_DEFAULTS.dash,
    visible: elementDisplay?.visible ?? true,
    label: elementDisplay?.line?.label ?? (false as const),
    ...(elementDisplay?.line ?? {}),
    vertex: { fixed: true, ...(elementDisplay?.point ?? {}) },
  };

  const line = elementFactory.line({
    a: vertex,
    b: mid,
    name: `${element.id}-med`,
    style: lineStyle,
  });

  jsxObjects.push(line.line, line.a, line.b);

  // --- Build ports ---
  const ports: PortMap = {
    length: () => {
      const v = getVertex() as PointPort;
      const e = getEdge() as EdgePort;
      const m = midpoint(e.start, e.end);
      return dist(v, m);
    },
    midpoint: () => {
      const e = getEdge() as EdgePort;
      return midpoint(e.start, e.end);
    },
  };

  return {
    jsxObjects,
    labels,
    ports,
    destroy: () => {
      const board = elementFactory.getContext().board;
      for (const label of labels) {
        board.removeObject(label);
      }
      for (const obj of jsxObjects) {
        board.removeObject(obj);
      }
    },
  };
}

// ---------------------------------------------------------------------------
// Math helpers
// ---------------------------------------------------------------------------

function midpoint(a: PointPort, b: PointPort): PointPort {
  return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
}

function dist(a: PointPort, b: PointPort): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}
